// Cash-balance benefit engine: from adopted credits to testing rates.
//
// For one participant-year this computes, rather than merely accepts:
//   - the pay credit and interest credit;
//   - the end-of-year hypothetical account balance;
//   - the accrued benefit (whole balance projected to NRA at the interest
//     credit and annuitized on the PLAN basis) and the normal-retirement
//     monthly benefit;
//   - the year's TESTING benefit (this year's pay credit projected and
//     annuitized on the plan basis — the normal accrual the general test
//     values), its §401(a)(26)-style accrual rate, and its equivalent
//     allocation on the testing basis (annuity revalued with the testing APR
//     and discounted back at the standard testing rate);
//   - the most-valuable (QJSA-route) monthly accrual: account → immediate J&S
//     at attained age on the plan basis → testing value (J&S APR ratio) →
//     accumulated to testing age → annuitized with the testing life APR
//     (verified against a professional proposal's MVAR worksheet);
//   - the §415(b) permitted benefit at the testing age and the limited
//     benefit when the accrued benefit exceeds it.
//
// Conventions match the parity-verified engine: end-of-year credits, no
// pre-retirement mortality, ages as supplied (the census system's convention
// is an input, not a guess). Factors carry provenance; a missing APR makes
// the result unavailable — nothing is extrapolated. MODELED, scenario-grade;
// not an actuarial certification.

import {
  annuityFactor,
  DEFAULT_ACTUARIAL_SET,
} from './actuarialAssumptions.js'
import { monthlyDollarLimitAtAge, monthlyCompLimit } from './limits415b.js'
import { CROSS_TEST_INTEREST_RATE } from './constants.js'

export function evaluateCashBalanceBenefit({
  payCredit = 0,
  openingBalance = 0,
  interestCredit = 0.05,
  age,
  testingAge = 65,
  comp,
  high3 = null,
  serviceYears = 0,
  participationYears = 1,
  dollarLimit415b = null,
  testingInterest = CROSS_TEST_INTEREST_RATE,
  planAprTable = null,
  testingAprTable = null,
  assumptionSet = DEFAULT_ACTUARIAL_SET,
  factors = [],
} = {}) {
  const compensation = Number(comp) || 0
  const attained = Number(age)
  const nra = Number(testingAge)
  const credit = Math.max(0, Number(payCredit) || 0)
  const opening = Math.max(0, Number(openingBalance) || 0)
  const icr = Number(interestCredit)
  if (!Number.isFinite(attained) || !Number.isFinite(nra) || compensation <= 0
    || !Number.isFinite(icr) || icr < 0) {
    return { status: 'unavailable', reason: 'invalid-inputs' }
  }
  const years = Math.max(0, nra - attained)
  const planApr = annuityFactor({ basis: 'plan', age: nra, customTable: planAprTable, assumptionSet })
  const testingApr = annuityFactor({ basis: 'testing', age: nra, customTable: testingAprTable, assumptionSet })
  if (!planApr || !testingApr) return { status: 'unavailable', reason: 'missing-apr-for-age' }
  factors.push(
    { basis: 'plan', form: 'life', age: Math.round(nra), ...planApr },
    { basis: 'testing', form: 'life', age: Math.round(nra), ...testingApr },
  )

  // ---- Account mechanics (end-of-year credit convention) ----
  const interestCreditAmount = opening * icr
  const hypotheticalBalance = opening * (1 + icr) + credit
  const projectedBalanceAtNra = hypotheticalBalance * ((1 + icr) ** years)

  // ---- Accrued benefit (whole balance) ----
  const accruedMonthlyBenefit = projectedBalanceAtNra / planApr.value

  // ---- Year's testing benefit (this year's credit only) ----
  const testingMonthlyBenefit = (credit * ((1 + icr) ** years)) / planApr.value
  const accrualRatePercent = (testingMonthlyBenefit * 12 / compensation) * 100
  const equivalentAllocation = testingMonthlyBenefit * testingApr.value / ((1 + testingInterest) ** years)

  // ---- Most-valuable (QJSA route) on the year's credit ----
  let mostValuableMonthly = null
  let mostValuableReason = null
  const jointPlan = annuityFactor({ basis: 'plan', form: 'joint', age: attained, assumptionSet })
  const jointTesting = annuityFactor({ basis: 'testing', form: 'joint', age: attained, assumptionSet })
  if (jointPlan && jointTesting) {
    factors.push(
      { basis: 'plan', form: 'joint', age: Math.round(attained), ...jointPlan },
      { basis: 'testing', form: 'joint', age: Math.round(attained), ...jointTesting },
    )
    const testingValue = credit * (jointTesting.value / jointPlan.value)
    mostValuableMonthly = (testingValue * ((1 + testingInterest) ** years)) / testingApr.value
  } else {
    mostValuableReason = 'missing-joint-apr-for-age'
  }

  // ---- §415(b) permitted benefit at the testing age ----
  let section415b = null
  if (Number(dollarLimit415b) > 0) {
    const dollar = monthlyDollarLimitAtAge({
      age: nra,
      participationYears,
      dollarLimit415b,
      assumptionSet,
      factors,
    })
    const compLimb = monthlyCompLimit({ comp: compensation, high3, serviceYears })
    if (dollar && compLimb) {
      const permittedMonthly = Math.min(dollar.value, compLimb.value)
      section415b = {
        permittedMonthly,
        limitBasis: compLimb.value <= dollar.value ? 'compensation' : 'dollar',
        high3Source: compLimb.high3Source,
        dollarAdjustment: dollar.adjustment,
        accruedExceedsLimit: accruedMonthlyBenefit > permittedMonthly + 1e-9,
        limitedMonthlyBenefit: Math.min(accruedMonthlyBenefit, permittedMonthly),
        reductionMonthly: Math.max(0, accruedMonthlyBenefit - permittedMonthly),
      }
    }
  }

  return {
    status: 'ok',
    payCredit: credit,
    interestCreditRate: icr,
    interestCreditAmount,
    openingBalance: opening,
    hypotheticalBalance,
    projectedBalanceAtNra,
    yearsToTestingAge: years,
    accruedMonthlyBenefit,
    normalRetirementMonthlyBenefit: accruedMonthlyBenefit,
    testingMonthlyBenefit,
    accrualRatePercent,
    equivalentAllocation,
    equivalentAllocationRatePercent: (equivalentAllocation / compensation) * 100,
    mostValuableMonthly,
    mostValuableReason,
    section415b,
  }
}
